import { Chip, Typography } from '@execonline-inc/execonline-ui';
import { Maybe } from 'maybeasy';
import { Competency } from '../../Types/SectionTemplates';

interface Props {
  competencies: Competency[];
  activeCompetencyId: Maybe<number>;
  onCompetencyClick: (id: number) => void;
}

const CompetenciesTab: React.FC<Props> = ({
  competencies,
  activeCompetencyId,
  onCompetencyClick,
}) => {
  if (competencies.length === 0) {
    return (
      <div className="w-full text-center py-8 text-muted-foreground">
        <Typography variant="bodySmall">No competencies available</Typography>
      </div>
    );
  }

  const handleClick = (compId: number) => {
    onCompetencyClick(compId);
    const element = document.getElementById(`competency-${compId}`);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="flex px-11 py-10" data-intercom-target="competencies-tab-page">
      {/* Left Column - Clickable competency pills */}
      <div
        className="max-h-[54vh] basis-4/12 overflow-y-auto lg:overflow-x-hidden flex flex-col gap-4"
        data-intercom-target="competency-list"
      >
        {competencies.map((comp) => {
          const isActive = activeCompetencyId.map((id) => id === comp.id).getOrElse(() => false);
          return (
            <Chip
              key={`left-${comp.id}`}
              color={isActive ? 'teal' : 'gray'}
              variant={isActive ? 'solid' : 'outlined'}
              radius="md"
              size="sm"
              onClick={() => handleClick(comp.id)}
              className="w-max cursor-pointer"
              data-intercom-target={`competency-chip-${comp.id}`}
            >
              {comp.name
                .map((name) => <span key={comp.id}>{name}</span>)
                .getOrElse(() => (
                  <></>
                ))}
            </Chip>
          );
        })}
      </div>

      {/* Right Column - Competency details */}
      <div
        className="ml-6 max-h-[54vh] basis-8/12 overflow-auto"
        data-intercom-target="competency-details"
      >
        {competencies.map((comp) => (
          <div
            key={comp.id}
            id={`competency-${comp.id}`}
            className="mb-6 border-b border-solid border-border pb-6 last:mb-0 last:border-b-0 last:pb-0"
            data-intercom-target={`competency-detail-${comp.id}`}
          >
            <h3 className="text-xl font-medium">
              {comp.name
                .map((name) => <span key={`name-${comp.id}`}>{name}</span>)
                .getOrElse(() => (
                  <></>
                ))}
            </h3>
            <p className="w-4/5 pt-4 text-muted-foreground">
              {comp.description
                .map((desc) => <span key={`desc-${comp.id}`}>{desc}</span>)
                .getOrElse(() => (
                  <></>
                ))}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompetenciesTab;
